import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { APP_CONFIG, AppConfig } from '@windsim/core/models';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly siteKey = this.getRecaptchaSiteKey();
  private isLoaded = new BehaviorSubject<boolean>(false);
  private isLoading = false;

  public isLoaded$ = this.isLoaded.asObservable();

  constructor(@Inject(APP_CONFIG) private config: AppConfig) {
    this.loadRecaptchaScript();
  }

  /**
   * Get the appropriate reCAPTCHA v3 site key based on environment
   */
  private getRecaptchaSiteKey(): string {
    // Fallback to environment-based logic
    const hostname = window.location.hostname;

    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      // Use Google's test key for local development - always passes validation
      return '6LeSgMwrAAAAAMfv4KKSVnK1QVA1BlxBjPzP-UwH';
    }
    // Check if reCAPTCHA configuration exists in app config
    if (this.config?.recaptcha?.siteKey) {
      return this.config.recaptcha.siteKey;
    }

    // Use production key for all deployed environments (including dev-accelerator)
    return '6Lc2rJwrAAAAAJYLh1yFWjHqQilKK6ui1H-dcijm';
  }

  /**
   * Load the reCAPTCHA script dynamically
   */
  private loadRecaptchaScript(): void {
    if (this.isLoading || this.isLoaded.value) {
      return;
    }

    this.isLoading = true;

    // Check if reCAPTCHA is already loaded
    if (window.grecaptcha) {
      this.isLoaded.next(true);
      this.isLoading = false;
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      this.isLoaded.next(true);
      this.isLoading = false;
    };

    script.onerror = () => {
      this.isLoading = false;
    };

    document.head.appendChild(script);
  }

  /**
   * Execute reCAPTCHA v3 and get token
   * Note: Token verification and scoring is handled on the backend
   *
   * @param action - The action name for this reCAPTCHA execution (e.g., 'registration', 'login', 'contact')
   * @returns Promise<string> - The reCAPTCHA token
   */
  public executeRecaptcha(action: string = 'registration'): Promise<string> {
    return new Promise((resolve, reject) => {
      // Validate action parameter (v3 requirement)
      if (!action || typeof action !== 'string') {
        reject(new Error('reCAPTCHA v3 requires a valid action parameter'));
        return;
      }

      if (!this.isLoaded.value) {
        reject(new Error('reCAPTCHA v3 script not loaded'));
        return;
      }

      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA v3 not available'));
        return;
      }

      // Ensure grecaptcha is ready before execution
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(this.siteKey, { action })
          .then((token: string) => {
            if (!token) {
              reject(new Error('reCAPTCHA v3 failed to generate token'));
              return;
            }

            resolve(token);
          })
          .catch((error: any) => {
            reject(new Error(`reCAPTCHA v3 execution failed: ${error.message || error}`));
          });
      });
    });
  }

  /**
   * Get the site key for manual implementation
   */
  public getSiteKey(): string {
    return this.siteKey;
  }

  /**
   * Check if reCAPTCHA is ready
   */
  public isReady(): boolean {
    return this.isLoaded.value && !!window.grecaptcha;
  }

  /**
   * Reset reCAPTCHA (for v2 checkbox, not needed for v3)
   */
  public reset(): void {
    if (window.grecaptcha && window.grecaptcha.reset) {
      window.grecaptcha.reset();
    }
  }
}
